import type { ContractFixtureEnvelope } from './types';

/**
 * Reads the real CP14-vendored fixtures in `contract/fixtures/` (landed 2026-07-20, replacing
 * CP21's `"draft": true` placeholders - see `contract/README.md`). Uses `import.meta.glob`
 * (pattern-based) - confirmed no code changes were needed here when the real fixtures replaced
 * the drafts, exactly as this file was designed for.
 */
const fixtureModules = import.meta.glob<ContractFixtureEnvelope>('/contract/fixtures/**/*.json', {
  eager: true,
  import: 'default',
});

export function listFixturePaths(): string[] {
  return Object.keys(fixtureModules);
}

export function getFixture(path: string): ContractFixtureEnvelope | undefined {
  return fixtureModules[path];
}

/** True now that CP14 has vendored the real fixtures into contract/fixtures/. */
export const CONTRACT_VENDORED = listFixturePaths().length > 0;

/**
 * Lightweight STRUCTURAL check (CP21) - still not full JSON Schema validation, even though
 * `contract/game-feed.v1.schema.json` now exists (CP14 vendored it 2026-07-20). Upgrading to real
 * `ajv`-based validation against that schema is a deliberate call for CP21/CP22 to make (it means
 * adding a new dependency to this repo), not something this vendoring pass should decide as a side
 * effect - left as-is, just corrected for the one real drift found against the real contract:
 * `season: null` is a VALID state (no season currently covers today), not a validation failure.
 */
export function validateFixture(fixture: unknown): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (typeof fixture !== 'object' || fixture === null) {
    return { valid: false, errors: ['fixture is not an object'] };
  }
  const f = fixture as Record<string, unknown>;

  if (typeof f.contractVersion !== 'number') errors.push('missing/invalid contractVersion');
  if (typeof f.generatedAt !== 'string') errors.push('missing/invalid generatedAt');
  if (f.season !== null && (typeof f.season !== 'object' || f.season === undefined)) {
    errors.push('invalid season (must be an object or null)');
  }
  if (!Array.isArray(f.events)) errors.push('missing/invalid events array');
  if (typeof f.world !== 'object' || f.world === null) {
    errors.push('missing/invalid world');
  } else {
    const world = f.world as Record<string, unknown>;
    if (typeof world.treesChoppedTotal !== 'number') errors.push('world.treesChoppedTotal missing/invalid');
    if (typeof world.treesPlantedTotal !== 'number') errors.push('world.treesPlantedTotal missing/invalid');
  }

  if (typeof f.sides !== 'object' || f.sides === null) {
    errors.push('missing/invalid sides');
  } else {
    const sides = f.sides as Record<string, unknown>;
    for (const key of ['beornings', 'lothwen'] as const) {
      const side = sides[key];
      if (typeof side !== 'object' || side === null) {
        errors.push(`sides.${key} missing/invalid`);
        continue;
      }
      const s = side as Record<string, unknown>;
      if (typeof s.totalPoints !== 'number') errors.push(`sides.${key}.totalPoints missing/invalid`);
      if (typeof s.sharePct !== 'number') errors.push(`sides.${key}.sharePct missing/invalid`);
    }
  }

  return { valid: errors.length === 0, errors };
}
